import { BoxDetailVariableProps } from '@/box/interface'
import { Column } from '@/shared/Column'
import {
  bcdDataType,
  binaryDataType,
  bitDataType,
  dataTypeRange,
  dataValidateRegex,
  decimalDataType,
  hexDataType,
  stringDataType
} from '@/shared/SelectData'
import { VariableItem } from '@/template/interface'
import { TemplateVariableTable } from '@/template/variable/table'
import { bcd2Number } from '@/utils/bcd'
import { getFormatMessage } from '@/utils/utils'
import { EditOutlined, SearchOutlined } from '@ant-design/icons'
import {
  Badge,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Tooltip
} from 'antd'
import { PresetStatusColorType } from 'antd/lib/_util/colors'
import React, { useEffect, useState } from 'react'

export const BoxDetailVariable: React.FC<BoxDetailVariableProps> = (
  props: BoxDetailVariableProps
) => {
  const {
    detailItem,
    contactTemplate,
    values,
    pagination,
    onValueSet,
    onVariablePushStatusChange,
    onVariableSearch
  } = props

  const [writeValueVisible, setWriteValueVisible] = useState<boolean>(false)
  const [variables, setVariables] = useState<VariableItem[]>(
    contactTemplate?.variables || []
  )
  const [selectVariable, setSelectVariable] = useState<VariableItem>()
  const [form] = Form.useForm()
  const [searchForm] = Form.useForm()
  const [loading, setLoading] = useState<boolean>(false)
  const formatMessage = getFormatMessage()
  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 4 }
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 }
    }
  }

  const columns: Column[] = [
    {
      index: 1,
      override: false,
      title: formatMessage({ id: 'app.box.detail.template.variable.status' }),
      render: (text: any, record: any) => {
        let status: PresetStatusColorType = 'warning'
        let statusDesc = formatMessage({ id: 'app.plcStatus' })
        if (detailItem?.connectionState === 1) {
          if (record.status == 0) {
            status = 'success'
            statusDesc = formatMessage({ id: `app.plcStatus.${record.status}` })
          }
          if (record.status > 0) {
            status = 'error'
            statusDesc = formatMessage({ id: `app.plcStatus.${record.status}` })
          }
        } else {
          status = 'error'
          statusDesc = formatMessage({ id: 'app.plcStatus.offline' })
        }

        return (
          <span>
            <Badge status={status} />
            {statusDesc}
          </span>
        )
      }
    },
    { index: 3, hidden: true },
    {
      index: 4,
      override: false,
      title: formatMessage({ id: 'app.template.variable.table.data' }),
      render: (text: any, record: any) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {record.privilege == 2 ? (
              <span style={{ flex: '1' }}>
                <Tooltip
                  title={formatMessage({
                    id: 'app.box.detail.template.variable.readonly'
                  })}
                >
                  <span style={{ color: '#c2d0dd', cursor: 'pointer' }}>0</span>
                </Tooltip>
              </span>
            ) : (
              <span style={{ flex: '1' }}>{record.value}</span>
            )}
            {[2, 6].includes(record.privilege) && (
              <Button
                type='link'
                disabled={detailItem?.connectionState !== 1}
                onClick={() => handleWriteValue(record)}
              >
                <EditOutlined />
              </Button>
            )}
          </div>
        )
      }
    },
    { index: 5, hidden: true }
  ]

  const handleWriteValue = (item: any) => {
    setSelectVariable(item)
    if (item.dataType === 0) {
      const data = {
        value: item.value
          ? item.value.toString() == '0'
            ? true
            : false
          : false
      }
      form.setFieldsValue(data)
    }
    setWriteValueVisible(true)
  }

  const handleWriteValueModalOk = () => {
    form.validateFields().then((item) => {
      if (onValueSet && selectVariable) {
        let value = item.value
        // bool类型
        if (bitDataType.includes(selectVariable.dataType!)) {
          value = +value //转成10进制(0 或 1)
        }
        // BCD码类型
        if (bcdDataType.includes(selectVariable.dataType!)) {
          if (selectVariable.fractionDigits) {
            value = bcd2Number(
              value * Math.pow(10, selectVariable.fractionDigits)
            ) // 转10进制
          } else {
            value = bcd2Number(value) // 转10进制
          }
        }
        // 16进制字符串
        if (hexDataType.includes(selectVariable.dataType!)) {
          value = parseInt(`0x${value}`, 16) // 转成10进制
        }
        // 二进制字符串
        if (binaryDataType.includes(selectVariable.dataType!)) {
          value = parseInt(value, 2) // 转成10进制
        }
        console.log(
          `Write value, ${selectVariable.name}: ${item.value} -> ${value}`
        )
        onValueSet(selectVariable, value)
      }
      setWriteValueVisible(false)
      form.resetFields()
    })
  }

  const handleVariablePushStatusChange = async (checked?: boolean) => {
    if (onVariablePushStatusChange) {
      try {
        setLoading(true)
        await onVariablePushStatusChange(checked ? 1 : 0)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleSearch = async () => {
    const values = await searchForm.validateFields()
    let newValues = contactTemplate?.variables || []

    if (values.status) {
      newValues = newValues.filter((x) => x['status'] === values.status)
    }

    if (values.deviceId) {
      newValues = newValues.filter((x) => x.deviceId === values.deviceId)
    }

    if (values.name) {
      newValues = newValues.filter(
        (x) =>
          x.name?.includes(values.name) || x.description?.includes(values.name)
      )
    }
    setVariables(newValues)
    onVariableSearch?.(values)
  }

  const handleClean = () => {
    searchForm.resetFields()
    setVariables(contactTemplate?.variables || [])
    onVariableSearch?.({})
  }

  const width = (dataType: number) => {
    if (binaryDataType.includes(dataType)) {
      return 500
    }
    return 350
  }

  const maxLimit = (item: VariableItem) => {
    let max = dataTypeRange[item.dataType || 0].maxValueLimit
    if (item.fractionDigits) {
      max = max / Math.pow(10, item.fractionDigits)
      return max
    }
    return max
  }

  const minLimit = (item: VariableItem) => {
    const min = dataTypeRange[item.dataType || 0].minValueLimit
    return min
  }

  useEffect(() => {
    setVariables(contactTemplate?.variables || [])
    searchForm.resetFields()
  }, [contactTemplate])

  useEffect(() => {
    if (values) {
      if (Array.isArray(values)) {
        const map: {
          [key: string]: { data: any; status: number; unit: string }
        } = {}
        values.forEach((x) => {
          /**
           * 格式1: [名称, 源数据, 状态]
           * 格式2: [名称, 源数据, 状态, 显示数据]
           * 格式3: [名称, 源数据, 状态, 显示数据, 单位]
           */
          if (x.length < 3) {
            if (x.length > 0) {
              console.log(`监控点上报格式错误: ${x[0]}`)
            } else {
              console.log(`数据格式错误`)
            }
          } else {
            map[x[0]] = {
              data: x.length > 3 ? x[3] : x[1], // 长度大于3 使用显示数据，否则使用源数据
              status: x[2],
              unit: x.length > 4 ? x[4] : '' // 长度大于4 则显示单位
            }
          }
        })
        const data = variables?.map((x: any) => {
          const vitem = map[x.name]
          if (vitem != undefined && vitem != null) {
            let value = vitem.data
            if (value === true || value === false) {
              value = +value
            }
            x.value = `${value.toString()} ${vitem.unit}`
            x.status = map[x.name].status
          }
          return x
        })
        setVariables(data)
      } else {
        const data = variables?.map((x: any) => {
          let value = values[x.name]
          if (value != undefined && value != null) {
            if (value === true || value === false) {
              value = +value
            }
            x.value = value.toString()
            x.status = 0
          }
          return x
        })
        setVariables(data)
      }
    }
  }, [values])

  return (
    <div>
      <Form form={searchForm} layout='inline' style={{ marginBottom: 5 }}>
        {/* <Form.Item name='status'>
          <Select
            placeholder={formatMessage({
              id: 'app.box.detail.template.variable.status.msg'
            })}
            allowClear
          >
            {[0, 1, 2].map((x) => (
              <Select.Option key={`app.plcStatus.${x}`} value={x}>
                {formatMessage({ id: `app.plcStatus.${x}` })}
              </Select.Option>
            ))}
          </Select>
        </Form.Item> */}
        <Form.Item name='deviceId'>
          <Select
            placeholder={formatMessage({
              id: 'app.box.detail.template.variable.plc.msg'
            })}
            allowClear
          >
            {contactTemplate?.plcs?.map((x) => (
              <Select.Option key={x.id} value={x.id!}>
                {x.alias}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name='name'>
          <Input
            placeholder={formatMessage({
              id: 'app.box.detail.template.variable.name.msg'
            })}
          />
        </Form.Item>
        <Form.Item>
          <Button type='link' onClick={handleClean}>
            {formatMessage({ id: 'app.template.toolbar.clean' })}
          </Button>
        </Form.Item>
        <Form.Item>
          <Button
            type='primary'
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            {formatMessage({ id: 'app.template.toolbar.search' })}
          </Button>
        </Form.Item>
        <Form.Item>
          <Space>
            <span>
              {formatMessage({
                id: 'app.box.detail.template.variable.pushStatus'
              })}
            </span>
            <Switch
              checkedChildren={formatMessage({
                id: 'app.box.detail.variable.pushStatus.on'
              })}
              unCheckedChildren={formatMessage({
                id: 'app.box.detail.variable.pushStatus.off'
              })}
              onChange={handleVariablePushStatusChange}
              loading={loading}
              disabled={detailItem?.connectionState !== 1}
              checked={detailItem?.enableItemPush === 1}
            />
          </Space>
        </Form.Item>
      </Form>
      <TemplateVariableTable
        columns={columns}
        data={variables}
        plcs={contactTemplate?.plcs}
        pagination={pagination}
      />
      <Modal
        destroyOnClose={true}
        maskClosable={false}
        title={formatMessage({ id: 'app.box.detail.variable.value.title' })}
        visible={writeValueVisible}
        onOk={handleWriteValueModalOk}
        onCancel={() => {
          form.resetFields()
          setWriteValueVisible(false)
        }}
        width={width(selectVariable?.dataType!)}
      >
        <Form form={form} {...formItemLayout}>
          {/** 位写值，按位索引写值 */}
          {(bitDataType.includes(selectVariable?.dataType!) ||
            selectVariable?.enabledIndex) && (
            <Form.Item
              label={formatMessage({
                id: 'app.box.detail.variable.value'
              })}
              name='value'
              valuePropName='checked'
            >
              <Switch />
            </Form.Item>
          )}
          {/** 字符串写值 */}
          {stringDataType.includes(selectVariable?.dataType!) && (
            <Form.Item
              label={formatMessage({
                id: 'app.box.detail.variable.value'
              })}
              name='value'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.box.detail.variable.value.required'
                  })
                }
              ]}
            >
              <Input
                maxLength={
                  selectVariable?.stringSetting
                    ? selectVariable.stringSetting.charCount
                    : 1
                }
                placeholder={formatMessage({
                  id: 'app.box.detail.variable.value.string'
                })}
              />
            </Form.Item>
          )}
          {/** 十进制写值 */}
          {decimalDataType.includes(selectVariable?.dataType!) &&
            !bcdDataType.includes(selectVariable?.dataType!) &&
            !selectVariable?.enabledIndex && (
              <Form.Item
                label={formatMessage({
                  id: 'app.box.detail.variable.value'
                })}
                name='value'
                rules={[
                  {
                    required: true,
                    message: formatMessage({
                      id: 'app.box.detail.variable.value.required'
                    })
                  }
                ]}
              >
                <InputNumber
                  max={maxLimit(selectVariable!)}
                  min={minLimit(selectVariable!)}
                  precision={selectVariable?.fractionDigits || 0}
                  placeholder={formatMessage({
                    id: 'app.box.detail.variable.value.decimal'
                  })}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}
          {/** 16进制写值 */}
          {hexDataType.includes(selectVariable?.dataType!) &&
            !selectVariable?.enabledIndex && (
              <Form.Item
                label={formatMessage({
                  id: 'app.box.detail.variable.value'
                })}
                name='value'
                rules={[
                  {
                    required: true,
                    message: formatMessage({
                      id: 'app.box.detail.variable.value.required'
                    })
                  },
                  {
                    pattern: dataValidateRegex[selectVariable?.dataType!],
                    message: formatMessage({
                      id: 'app.box.detail.variable.value.required.range',
                      parameters: {
                        min: dataTypeRange[selectVariable?.dataType || 0]
                          .minValueLimit,
                        max: dataTypeRange[
                          selectVariable?.dataType || 0
                        ].maxValueLimit
                          .toString(16)
                          .toUpperCase()
                      }
                    })
                  }
                ]}
              >
                <Input
                  placeholder={formatMessage({
                    id: 'app.box.detail.variable.value.hex'
                  })}
                />
              </Form.Item>
            )}
          {/** 二进制写值 */}
          {binaryDataType.includes(selectVariable?.dataType!) &&
            !selectVariable?.enabledIndex && (
              <Form.Item
                label={formatMessage({
                  id: 'app.box.detail.variable.value'
                })}
                name='value'
                rules={[
                  {
                    required: true,
                    message: formatMessage({
                      id: 'app.box.detail.variable.value.required'
                    })
                  },
                  {
                    pattern: dataValidateRegex[selectVariable?.dataType!],
                    message: formatMessage({
                      id: 'app.box.detail.variable.value.required.range',
                      parameters: {
                        min: dataTypeRange[selectVariable?.dataType || 0]
                          .minValueLimit,
                        max: (
                          dataTypeRange[selectVariable?.dataType || 0]
                            .maxValueLimit >>> 0
                        ).toString(2)
                      }
                    })
                  }
                ]}
              >
                <Input
                  placeholder={formatMessage({
                    id: 'app.box.detail.variable.value.binary'
                  })}
                />
              </Form.Item>
            )}
          {/** BCD码写值 */}
          {bcdDataType.includes(selectVariable?.dataType!) &&
            !selectVariable?.enabledIndex && (
              <Form.Item
                label={formatMessage({
                  id: 'app.box.detail.variable.value'
                })}
                name='value'
                rules={[
                  {
                    required: true,
                    message: formatMessage({
                      id: 'app.box.detail.variable.value.required'
                    })
                  }
                ]}
              >
                <InputNumber
                  max={maxLimit(selectVariable!)}
                  min={minLimit(selectVariable!)}
                  precision={selectVariable?.fractionDigits || 0}
                  placeholder={formatMessage({
                    id: 'app.box.detail.variable.value.bcd'
                  })}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}
        </Form>
      </Modal>
    </div>
  )
}
